/**
 * PageSetupValidator - Utility for validating PageSetup objects
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for validating PageSetup structures,
 * ensuring required properties exist before use.
 */

import type { PageSetup } from '../../../core/types/Typesetter';

/**
 * Validation result for PageSetup
 */
export interface PageSetupValidationResult {
  isValid: boolean;
  error?: string;
  diagnostics?: {
    hasPageSetup: boolean;
    hasContentRect: boolean;
    pageSetupKeys: string[];
  };
}

/**
 * Validate PageSetup structure
 * 
 * Ensures that PageSetup has the required content_rect_fp property
 * before it can be used for pagination.
 * 
 * @param pageSetup - PageSetup object to validate
 * @returns Validation result with diagnostics
 */
export function validatePageSetup(pageSetup: PageSetup | null | undefined): PageSetupValidationResult {
  if (!pageSetup) {
    return {
      isValid: false,
      error: 'Invalid pageSetup: pageSetup is null or undefined',
      diagnostics: {
        hasPageSetup: false,
        hasContentRect: false,
        pageSetupKeys: []
      }
    };
  }

  if (!pageSetup.content_rect_fp) {
    return {
      isValid: false,
      error: 'Invalid pageSetup: content_rect_fp is missing. Page setup may not be initialized.',
      diagnostics: {
        hasPageSetup: true,
        hasContentRect: false,
        pageSetupKeys: Object.keys(pageSetup)
      }
    };
  }

  return {
    isValid: true
  };
}

/**
 * Validate PageSetup and throw error if invalid
 * 
 * Validates PageSetup and throws an error with diagnostics if invalid.
 * This is a convenience function for validation with immediate error throwing.
 * 
 * @param pageSetup - PageSetup object to validate
 * @throws Error if PageSetup is invalid
 */
export function validatePageSetupOrThrow(pageSetup: PageSetup | null | undefined): void {
  const result = validatePageSetup(pageSetup);
  
  if (!result.isValid) {
    console.error('❌ [TypesetterWorkerClient] Invalid pageSetup:', result.diagnostics);
    throw new Error(result.error || 'Invalid pageSetup');
  }
}


